// src/utils/ai/aiHistoryFormatters.js
//
// Pure formatters for serializing assistant messages into Anthropic conversation
// history. Centralized here so every history rebuild produces the same string
// for the same (text, action, actionStatus, actionResult, locale) tuple — no
// drift between callers and no missing lifecycle states.

import {
  historyJobLoggedSuffix,
  historyEditManuallySuffix,
} from './aiAssistantHistoryStrings';

const isEs = (locale) => String(locale || 'en').toLowerCase().startsWith('es');

export function actionPresenceMarker(action, locale) {
  if (!action || typeof action !== 'object' || !action.type) return '';
  const type = String(action.type);
  const customerHint =
    action.customer_id ? ` (customer ${String(action.customer_id).slice(0, 8)})` : '';
  return isEs(locale)
    ? `[Acción propuesta: ${type}${customerHint}]`
    : `[Action proposed: ${type}${customerHint}]`;
}

export function actionStatusMarker(actionStatus, actionResult, locale) {
  switch (actionStatus) {
    case 'completed':
      return historyJobLoggedSuffix(locale, actionResult);
    case 'cancelled':
    case 'dismissed':
      return historyEditManuallySuffix(locale);
    case 'processing':
      return isEs(locale) ? ' [Acción en proceso]' : ' [Action processing]';
    case 'failed': {
      const reason =
        actionResult && typeof actionResult === 'object' && typeof actionResult.error === 'string'
          ? `: ${actionResult.error}`
          : '';
      return isEs(locale) ? ` [Acción fallida${reason}]` : ` [Action failed${reason}]`;
    }
    case 'pending':
    case null:
    case undefined:
    default:
      return '';
  }
}

export function serializeMessageForHistory(message, locale) {
  if (!message || message.isError) return null;
  if (message.role !== 'user' && message.role !== 'assistant') return null;

  const role = message.role === 'user' ? 'user' : 'assistant';
  const baseText = typeof message.text === 'string' ? message.text : '';
  const presence = role === 'assistant' && !baseText ? actionPresenceMarker(message.action, locale) : '';
  const head = baseText || presence;
  if (!head) return null;

  const status = role === 'assistant' ? actionStatusMarker(message.actionStatus, message.actionResult, locale) : '';
  return { role, content: head + status };
}
